import { Component, OnDestroy, OnInit } from '@angular/core'
import { BettingSectionComponent } from './betting-section/betting-section.component'
import { CardDetail } from '@core/interfaces'
import { AccountService, WebSocketService } from '@core/services'
import { CommonModule } from '@angular/common'
import { ModeEnum } from '@core/enums'
import { BetTypeEnum, CardTypeEnum } from '@shared/enums'
import { BetResultDto, BuyInUpdateDto, InitGameDataDto } from '@core/dtos'
import { GameHistoryMobileComponent } from './game-history-mobile/game-history-mobile.component'
import { DomSanitizer } from '@angular/platform-browser'
import { PlayerService } from '@shared/services'
import {
  AutoBettingService,
  BetService,
  ClassicService,
  RushPopupService,
} from '@rush-shared/services'
import { StreamComponent } from './stream/stream.component'
import { PotComponent } from './pot/pot.component'
import { DeckCardComponent } from './deck-card/deck-card.component'
import { filter, map, Subscription } from 'rxjs'
import { FormsModule } from '@angular/forms'
import { GameHttp, UserAccountHttp } from '@core/https'
import { BetHeaderComponent } from './bet-header/bet-header.component'
import {
  BetConfirmationAlertComponent,
  PleaseStandByComponent,
  WinnerAlertComponent,
} from '@rush-shared/components'
import { BetHelper } from '@shared/utils'
import { BuyInAlertComponent } from '@rush-shared/components/alerts/buy-in-alert/buy-in-alert.component'

@Component({
  selector: 'app-classic-game',
  standalone: true,
  imports: [
    CommonModule,
    GameHistoryMobileComponent,
    StreamComponent,
    BettingSectionComponent,
    PotComponent,
    DeckCardComponent,
    FormsModule,
    BetHeaderComponent,
    PleaseStandByComponent,
    BetConfirmationAlertComponent,
    WinnerAlertComponent,
    BuyInAlertComponent,
  ],
  providers: [WebSocketService],
  templateUrl: './classic-game.component.html',
  styleUrl: './classic-game.component.sass',
})
export class ClassicGameComponent implements OnInit, OnDestroy {
  CardTypeEnum = CardTypeEnum

  betConfirmed = false

  buyInConfirmed = false

  get currentBetType() {
    return this._playerService.playerBet.betTypeId!
  }

  get currentBetChips() {
    return this._playerService.playerBet.chips
  }

  get betLocked(): boolean {
    if (this._playerService.playerBet.chips > 0 && this._playerService.isBetLock) {
      this.betConfirmed = true
      return true
    }

    return false
  }

  private get playerId(): string {
    return this._accountService.currentUserId
  }

  get authenticated() {
    return this._accountService.authenticated
  }
  get dealerName() {
    return this._classicService.activeDealer.name
  }

  get autoBettingIsDisable() {
    return (
      this._autoBettingService.disableAutoBetting ||
      (this._playerService.currentSeat !== undefined && this._playerService.currentSeat !== null)
    )
  }
  get showAutoBettingToggle() {
    return this._autoBettingService.validConfigsCount > 0
  }

  get standby(): boolean {
    return this._classicService.mode$.value === ModeEnum.Paused
  }

  get showRushWinnerResult() {
    return this.gameResult !== undefined && this.totalEarning.earned > 0
  }

  countdown: number
  playerCards: CardDetail[]
  showMaverickCard: boolean
  showPlayerCards: boolean
  maverickCard: CardDetail[]
  tableName: string
  dealerId: string

  totalEarning = {
    earned: 0,
  }

  gameResult: BetResultDto | undefined

  private _subscriiption = new Subscription()

  constructor(
    public _playerService: PlayerService,
    private _webSocketService: WebSocketService,
    private _userAccountHttp: UserAccountHttp,
    private _gameHttp: GameHttp,
    private _accountService: AccountService,
    private _sanitizer: DomSanitizer,
    public _classicService: ClassicService,
    private _rushPopupService: RushPopupService,
    public _autoBettingService: AutoBettingService,
    private _betService: BetService,
  ) {}

  ngOnInit(): void {
    this._classicService.activeDealer$.subscribe((dealer) => {
      this._classicService.loadInitData()

      this.reloadInitData()

      this.dealerId = dealer.id

      this._playerService.setActiveDealerById(this.dealerId)

      this._gameHttp.getDealerTableInfo(this.dealerId).subscribe((tableInfo) => {
        this._playerService.tableInfo = tableInfo
      })
    })

    this._userAccountHttp.getUserById(this.playerId).subscribe((user) => {
      this._playerService.setActivePlayer({
        id: user.id,
        name: user.fullName,
        chips: user?.chips || 0,
      })
    })

    this.getAutoBetConfigs()

    this._playerService.buyInClick$.subscribe(() => {
      this.buyInConfirmed = true
    })
  }

  getAutoBetConfigs() {
    this._autoBettingService.reloadConfigs()
  }

  ngOnDestroy(): void {
    this._playerService.clearBetResult()
    this._playerService.clearCurrentSeat()
    this._webSocketService.closeAllListeners()
    this._subscriiption.unsubscribe()
  }

  reloadInitData() {
    this._classicService.initData$.definedValueObserver
      .pipe(map((i) => i as InitGameDataDto))
      .subscribe({
        next: (i) => {
          this.tableName = i.tableName
          this._playerService.potBonus = i.pot?.amount || 0
          this._playerService.streamUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
            i.streamUrl,
          )
          this._playerService.strStreamUrl = i.streamUrl

          if (i.seat) {
            this._playerService.setSeat(i.seat)
          }
          if (i.playerBet) {
            this._playerService.playerBet = i.playerBet
          }

          this._playerService.isBetLock = i.playerBet?.isLocked === true
          this._playerService.minBet = i.minBet
          this._playerService.maxBet = i.maxBet
          this._playerService.foldCost = i.foldCost
          this._playerService.buyInCost = i.buyInCost
          this._playerService.gameHistory = i.gameHistory
          this._playerService.currentSeatNoFromDealer = i.gameConfig.seatNo
          this._classicService.setPlayingSeatNo(i.gameConfig.seatNo)
          this._classicService.setEasyPick(i.minBet, i.maxBet)
          this._classicService.setTablePot(i.pot.amount)
          this._classicService.setTableMaxBet(i.maxBet)

          this._classicService.setMode(i.gameConfig.mode)
          this._playerService.allowBuyIn =
            i.gameConfig.mode === ModeEnum.BuyInOpen && !i.seat ? true : false

          if (i.gameConfig.firstCard && i.gameConfig.secondCard) {
            this._classicService.setDeckCards([i.gameConfig.firstCard, i.gameConfig.secondCard])
            this.playerCards = [i.gameConfig.firstCard, i.gameConfig.secondCard]
            if (i.gameConfig.mode === ModeEnum.PlayerBets) {
              this.showPlayerCards = true
            }
          }

          if (i.gameConfig.maverickCard && this._classicService.mode$.value === ModeEnum.Result) {
            this.maverickCard = [i.gameConfig.maverickCard]
            if (i?.gameConfig.mode === ModeEnum.Result) {
              this.showMaverickCard = true
            }
          }

          if (
            this.playerCards &&
            (this._classicService.mode$.value === ModeEnum.PlayerBets ||
              this._classicService.mode$.value === ModeEnum.MaverickCard)
          ) {
            const [firstCard, secondCard] = this.playerCards
            if (firstCard.number === secondCard.number) {
              this._playerService.isPair = true
            } else {
              this._playerService.isPair = false
            }
          }

          this._playerService.invalidDealer = false
          this.listenToSocket()
        },
        error: (err) => {
          this._playerService.invalidDealer = true
        },
      })
  }

  listenToSocket() {
    this._webSocketService.listenToRushClassicMaxBet(
      this._classicService.activeTable.id,
      ({ maxBets }) => {
        const data = maxBets.find((i) => this._playerService.currentSeat?.seatNo === i.seatNo)

        if (data) {
          this.maxBetUpdate(data.maxBet)
        }
      },
    )

    this._webSocketService.listenToBetResult(this.playerId, (result: BetResultDto) => {
      this.gameResult = result

      this.totalEarning = {
        earned: result.earnedChips,
      }

      this._playerService.setBetResult({
        betTypeId: result.result,
        chips: result.currentChips,
        isWinner: result.isWinner,
        playerId: result.playerId,
        earnedChips: result.earnedChips,
        seatNo: this._playerService.currentSeat?.seatNo || 0,
      })
    })

    this._webSocketService.listenToBuyInLocked(this.playerId, (buyInData: BuyInUpdateDto) => {
      this._playerService.allowBuyIn = !buyInData.locked
      this._playerService.setSeat(buyInData.seat)
    })

    this._webSocketService.listenToPlayerCardsConfirmation(
      this.dealerId,
      ({ isConfirm, cards }) => {
        if (isConfirm) {
          this.showPlayerCards = true
          this._playerService.isBetLock = false
        }

        const [firstCard, secondCard] = cards
        if (cards.length > 0) {
          if (firstCard.number === secondCard.number) {
            this._playerService.isPair = true
          } else {
            this._playerService.isPair = false
          }
        }
      },
    )

    this._webSocketService.listenToMaverickCardsConfirmation(this.dealerId, ({ isConfirm }) => {
      if (isConfirm) {
        this.showMaverickCard = true
      }
    })

    this._webSocketService.listenToCountdown(this.dealerId, (timeRemaining: number) => {
      this.countDownCallback(timeRemaining)

      if (this._classicService.mode$.value === ModeEnum.Result && timeRemaining < 1) {
        this._playerService.clearBetResult()
      }
    })

    this._webSocketService.listenToMode(this.dealerId, ({ mode, currentSeatNo, roundNo }) => {
      this._classicService.setMode(mode)
      this._playerService.currentSeatNoFromDealer = currentSeatNo

      this._classicService.setPlayingSeatNo(currentSeatNo)
      this._classicService.setRoundNo(roundNo)

      this._playerService.allowBuyIn = mode === ModeEnum.BuyInOpen ? true : false

      if (mode === ModeEnum.Setup) {
        this._playerService.clearCurrentSeat()
        this._classicService.resetPlayingSeatNo()
      }

      // this._autoBettingService.setDisableAutoBetting(
      //   mode !== ModeEnum.Setup && mode !== ModeEnum.Result && mode !== ModeEnum.BuyInOpen,
      // )
    })

    this._webSocketService.listenToRemoveSeatNotif(this.playerId, (i) => {
      this._rushPopupService.setPopupInfo('Cancelled', 'You are the only seated on this table.')
      this._rushPopupService.showDangerPopup()
      this._playerService.setSeat(undefined)
    })

    this._webSocketService.listenToPlayerBetUpdate(this.playerId, (i) => {
      this._playerService.playerBet = i.playerBet
    })

    this._webSocketService.listenToGameHistory(this.dealerId, (gameHistory) => {
      this._playerService.gameHistory = gameHistory
    })
  }

  private resetState() {
    this.playerCards = []
    this._classicService.resetDeckCards()
    this.maverickCard = []
    this.showPlayerCards = false
    this.showMaverickCard = false
    this._playerService.isPair = false

    if (this._playerService.playerBet) {
      this._playerService.playerBet.chips = 0
      this._playerService.playerBet.isLocked = false
    }
  }

  private countDownCallback(timeRemaining: number) {
    switch (this._classicService.mode$.value) {
      case ModeEnum.PlayerBets:
        this.showPlayerCards = true
        break
      case ModeEnum.Result:
        this.showPlayerCards = true
        this.showMaverickCard = true
        if (timeRemaining === 0) {
          this.resetState()
        }
        break
      case ModeEnum.MaverickCard:
        if (this._playerService.playerBet.chips > 0 && !this._playerService.isBetLock) {
          this._playerService.initializeBet()
        }
        break
      default:
        break
    }
  }

  private maxBetUpdate(maxBet: number) {
    this._playerService.maxBet = maxBet
    this._classicService.setEasyPick(this._classicService.activeTable.minBet, maxBet)
    this._classicService.setTableMaxBet(maxBet)
  }

  toggleAutoBetting() {
    this._autoBettingService.setEnable(this._autoBettingService.enableAutoBetting)
  }

  hideRushWinnerResult() {
    this.gameResult = undefined
    this.totalEarning.earned = 0
  }

  getBetTypeKey(key: BetTypeEnum | undefined) {
    return BetHelper.getBetTypeKey(key!)
  }
}
